import { useState, useCallback, useEffect, useRef } from "react";
import { chatAPI } from "../services/api.js";
import {
  MODELS_REFRESH_MS,
  dropLegacyModelChoice,
  isModelUnavailable,
  readModelChoice,
  resolveSelection,
  sameModels,
  selectionNotice,
  shouldRefreshOnReturn,
  writeModelChoice,
} from "../utils/modelAvailability.js";

// Le simple accès à la propriété lève quand le navigateur bloque le stockage (iframe tierce)
function storageOf(name) {
  try {
    return window[name] ?? null;
  } catch {
    return null;
  }
}

/**
 * Modèles proposés par le serveur et modèle sélectionné.
 * Contrat v1.6 : la liste porte la disponibilité de chaque modèle ; elle est relue toutes
 * les 60 s quand l'onglet est visible, au retour sur l'onglet et sur demande
 * (refreshModels, ex. après une erreur MODEL_UNAVAILABLE ou au retour du réseau). Un modèle
 * indisponible ne peut pas être choisi.
 * Modèle par défaut dynamique (décision du 6 octobre 2026) : sans choix explicite, la
 * sélection suit le defaultId du serveur à chaque relecture (Qwen tant qu'il est
 * disponible) ; un choix explicite ne vaut que pour la session de l'onglet (sessionStorage)
 * et cède la place au défaut du serveur si son modèle devient indisponible. Chaque bascule
 * qui ne vient pas de l'utilisateur est annoncée par onNotice({text, tone}).
 * @param {{onNotice?: (notice: {text: string, tone: string}) => void}} options
 */
export function useModels({ onNotice = null } = {}) {
  const [models, setModels] = useState([]);
  const [selectedModelId, setSelectedModelId] = useState(null);
  const lastFetchRef = useRef(0);
  const inFlightRef = useRef(null);
  const mountedRef = useRef(true);
  // État lu par load et selectModel sans en dépendre (callbacks stables)
  const modelsRef = useRef([]);
  const selectedRef = useRef(null);
  // Choix explicite de la session (lu au montage), ou null
  const choiceRef = useRef(null);
  const onNoticeRef = useRef(onNotice);
  onNoticeRef.current = onNotice;

  // quiet : relecture en tâche de fond (échec silencieux, liste vide ignorée)
  const load = useCallback((quiet) => {
    if (inFlightRef.current) return inFlightRef.current;
    lastFetchRef.current = Date.now();
    const request = chatAPI
      .getModels({ quiet })
      .then((data) => {
        if (!mountedRef.current) return;
        const list = Array.isArray(data?.models) ? data.models : [];
        // Serveur qui redémarre : une liste vide passagère ne change rien
        if (quiet && list.length === 0) return;
        const explicitId = choiceRef.current;
        const selection = resolveSelection({ models: list, defaultId: data?.defaultId ?? null, explicitId });
        if (selection.dropped) {
          // Choix explicite retiré de la configuration ou indisponible : oublié, la
          // sélection suit désormais le modèle par défaut du serveur
          choiceRef.current = null;
          writeModelChoice(storageOf("sessionStorage"), null);
        }
        const notice = selectionNotice({
          previousId: selectedRef.current,
          nextId: selection.id,
          previousModels: modelsRef.current,
          models: list,
          lostChoiceId: selection.dropped ? explicitId : null,
        });
        modelsRef.current = list;
        selectedRef.current = selection.id;
        setModels((previous) => (sameModels(previous, list) ? previous : list));
        setSelectedModelId(selection.id);
        if (notice) onNoticeRef.current?.(notice);
      })
      .catch((error) => {
        // Sans liste de modèles, le chat fonctionne sans modelId (modèle par défaut du serveur)
        if (!quiet) console.error("Failed to load models:", error);
      })
      .finally(() => {
        inFlightRef.current = null;
      });
    inFlightRef.current = request;
    return request;
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    // Ancien choix persistant supprimé (migration), choix de la session relu
    dropLegacyModelChoice(storageOf("localStorage"));
    choiceRef.current = readModelChoice(storageOf("sessionStorage"));
    load(false);
    return () => {
      mountedRef.current = false;
    };
  }, [load]);

  // Rafraîchissement : toutes les 60 s onglet visible, et au retour sur l'onglet
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const visible = () => document.visibilityState !== "hidden";
    const timer = setInterval(() => {
      if (visible()) load(true);
    }, MODELS_REFRESH_MS);
    const onReturn = () => {
      if (shouldRefreshOnReturn({ visible: visible(), lastFetchAt: lastFetchRef.current, now: Date.now() })) load(true);
    };
    window.addEventListener("focus", onReturn);
    document.addEventListener("visibilitychange", onReturn);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", onReturn);
      document.removeEventListener("visibilitychange", onReturn);
    };
  }, [load]);

  const refreshModels = useCallback(() => load(true), [load]);

  // Choix explicite : retenu pour la session de l'onglet seulement
  const selectModel = useCallback((modelId) => {
    const model = modelsRef.current.find((m) => m.id === modelId);
    // Modèle inconnu ou indisponible : le choix courant est conservé
    if (!model || isModelUnavailable(model)) return;
    choiceRef.current = modelId;
    writeModelChoice(storageOf("sessionStorage"), modelId);
    selectedRef.current = modelId;
    setSelectedModelId(modelId);
  }, []);

  const selectedModel = models.find((m) => m.id === selectedModelId) ?? null;

  return { models, selectedModel, selectModel, refreshModels };
}
